import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle } from "@phosphor-icons/react";
import ImageUploader from "@/components/ImageUploader";
import CsvInput from "./CsvInput";

const EMPTY = {
  title: "", description: "", listing_type: "buy", property_type: "apartment",
  price: 0, price_period: "", currency: "AED", bedrooms: 0, bathrooms: 0, area_sqft: 0,
  community: "", sub_community: "", city: "Dubai", address: "",
  latitude: null, longitude: null,
  amenities: [], features: [], furnished: "unfurnished", view: "",
  developer: "", completion_date: "", payment_plan: "", images: [],
  video_url: "", tour_url: "", brochure_url: "", floor_plan_url: "",
  faqs: [], permit_number: "", featured: false,
};

export default function ListingForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.get(`/listings/${id}`, { params: { raw: true } }).then((r) => setF({ ...EMPTY, ...r.data }));
  }, [id]);

  function set(k, v) { setF((p) => ({ ...p, [k]: v })); }

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...f,
        price: Number(f.price), bedrooms: Number(f.bedrooms), bathrooms: Number(f.bathrooms),
        area_sqft: Number(f.area_sqft),
        latitude: f.latitude ? Number(f.latitude) : null,
        longitude: f.longitude ? Number(f.longitude) : null,
      };
      if (id) await api.put(`/listings/${id}`, payload);
      else await api.post("/listings", payload);
      toast.success("Saved");
      nav("/admin/listings");
    } catch (err) {
      toast.error("Save failed");
    } finally { setBusy(false); }
  }

  async function generateDesc() {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/describe-listing", { listing: f });
      set("description", data.description);
      toast.success("Description generated");
    } catch { toast.error("Generation failed — retry in a moment."); }
    finally { setAiBusy(false); }
  }

  function updateFaq(i, field, v) {
    const copy = [...(f.faqs || [])];
    copy[i] = { ...(copy[i] || {}), [field]: v };
    set("faqs", copy);
  }
  function addFaq() { set("faqs", [...(f.faqs || []), { q: "", a: "" }]); }
  function removeFaq(i) { set("faqs", (f.faqs || []).filter((_, idx) => idx !== i)); }

  return (
    <div className="p-10" data-testid="admin-listing-form">
      <div className="mb-8">
        <div className="text-xs tracking-[0.24em] uppercase text-gray-500 mb-1">Inventory</div>
        <h1 className="font-serif text-4xl">{id ? "Edit listing" : "New listing"}</h1>
      </div>
      <form onSubmit={save} className="grid md:grid-cols-2 gap-6 max-w-6xl">
        <FormField label="Title" value={f.title} onChange={(v) => set("title", v)} required testId="lf-title" />
        <FormField label="Community" value={f.community} onChange={(v) => set("community", v)} required testId="lf-community" />
        <FormField label="Sub community" value={f.sub_community} onChange={(v) => set("sub_community", v)} testId="lf-sub" />
        <FormField label="Address" value={f.address} onChange={(v) => set("address", v)} testId="lf-address" />

        <FormField label="Latitude" type="number" value={f.latitude ?? ""} onChange={(v) => set("latitude", v)} testId="lf-lat" />
        <FormField label="Longitude" type="number" value={f.longitude ?? ""} onChange={(v) => set("longitude", v)} testId="lf-lng" />

        <FormSelect label="Listing type" value={f.listing_type} onChange={(v) => set("listing_type", v)} options={["buy","rent","off-plan","commercial"]} testId="lf-listtype" />
        <FormSelect label="Property type" value={f.property_type} onChange={(v) => set("property_type", v)} options={["apartment","villa","townhouse","penthouse","office","plot"]} testId="lf-proptype" />

        <FormField label="Price (AED)" type="number" value={f.price} onChange={(v) => set("price", v)} testId="lf-price" />
        <FormSelect label="Price period" value={f.price_period || ""} onChange={(v) => set("price_period", v)} options={["", "yearly", "monthly"]} testId="lf-period" />

        <FormField label="Bedrooms" type="number" value={f.bedrooms} onChange={(v) => set("bedrooms", v)} testId="lf-beds" />
        <FormField label="Bathrooms" type="number" value={f.bathrooms} onChange={(v) => set("bathrooms", v)} testId="lf-baths" />
        <FormField label="Area (sqft)" type="number" value={f.area_sqft} onChange={(v) => set("area_sqft", v)} testId="lf-area" />
        <FormSelect label="Furnished" value={f.furnished || "unfurnished"} onChange={(v) => set("furnished", v)} options={["unfurnished","semi","furnished"]} testId="lf-furn" />

        <FormField label="View" value={f.view || ""} onChange={(v) => set("view", v)} testId="lf-view" />
        <FormField label="Developer" value={f.developer || ""} onChange={(v) => set("developer", v)} testId="lf-dev" />
        <FormField label="Completion date" value={f.completion_date || ""} onChange={(v) => set("completion_date", v)} testId="lf-comp" />
        <FormField label="Payment plan" value={f.payment_plan || ""} onChange={(v) => set("payment_plan", v)} testId="lf-pay" />
        <FormField label="Permit number (RERA)" value={f.permit_number || ""} onChange={(v) => set("permit_number", v)} testId="lf-permit" />
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-gray-500 block mb-2">Amenities (comma separated)</span>
          <CsvInput className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-[#050d19]" value={f.amenities} onChange={(v) => set("amenities", v)} data-testid="lf-amen" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-gray-500 block mb-2">Features (comma separated)</span>
          <CsvInput className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-[#050d19]" value={f.features} onChange={(v) => set("features", v)} data-testid="lf-features" />
        </label>

        <FormField label="Video URL (Vimeo / YouTube)" value={f.video_url || ""} onChange={(v) => set("video_url", v)} testId="lf-video" />
        <FormField label="360° tour URL (Matterport / Kuula)" value={f.tour_url || ""} onChange={(v) => set("tour_url", v)} testId="lf-tour" />
        <FormField label="Brochure PDF URL" value={f.brochure_url || ""} onChange={(v) => set("brochure_url", v)} testId="lf-brochure" />
        <FormField label="Floor plan URL" value={f.floor_plan_url || ""} onChange={(v) => set("floor_plan_url", v)} testId="lf-floor" />

        <div className="md:col-span-2">
          <label className="flex items-center gap-3 text-sm text-gray-700">
            <input type="checkbox" checked={!!f.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="lf-featured" />
            Featured on homepage
          </label>
        </div>

        <div className="md:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs uppercase tracking-wider text-gray-500">Description</label>
            <button
              type="button" onClick={generateDesc} disabled={aiBusy}
              className="flex items-center gap-2 text-xs bg-white text-white px-3 py-1.5 hover:bg-[#001588] hover:text-black transition-colors disabled:opacity-50"
              data-testid="lf-ai-desc"
            >
              <Sparkle size={14} weight="fill" /> {aiBusy ? "Generating…" : "AI generate"}
            </button>
          </div>
          <textarea
            value={f.description} onChange={(e) => set("description", e.target.value)} rows="6" required
            className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-[#050d19]"
            data-testid="lf-description"
          />
        </div>

        {/* Images with drag/drop reorder */}
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-wider text-gray-500 block mb-3">Images (drag to reorder)</label>
          <ImageUploader images={f.images} onChange={(next) => set("images", next)} testId="lf-image-uploader" />
        </div>

        {/* FAQs */}
        <div className="md:col-span-2 border-t border-gray-200 pt-6">
          <div className="flex items-center justify-between mb-4">
            <label className="text-xs uppercase tracking-wider text-gray-500">FAQs (for SEO / AEO)</label>
            <button type="button" onClick={addFaq} className="text-xs border border-gray-300 px-3 py-1.5 hover:border-black" data-testid="lf-add-faq">+ Add FAQ</button>
          </div>
          {(f.faqs || []).map((faq, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3" data-testid={`lf-faq-${i}`}>
              <input placeholder="Question" value={faq.q || ""} onChange={(e) => updateFaq(i, "q", e.target.value)} className="border border-gray-300 px-3 py-2" />
              <div className="flex gap-2">
                <input placeholder="Answer" value={faq.a || ""} onChange={(e) => updateFaq(i, "a", e.target.value)} className="flex-1 border border-gray-300 px-3 py-2" />
                <button type="button" onClick={() => removeFaq(i)} className="border border-gray-300 px-3 hover:border-red-500 hover:text-red-500" data-testid={`lf-remove-faq-${i}`}>×</button>
              </div>
            </div>
          ))}
          <p className="text-xs text-gray-500">Leave empty to use default auto-generated FAQs.</p>
        </div>

        <div className="md:col-span-2 flex gap-3 pt-6 border-t border-gray-200">
          <button type="submit" disabled={busy} className="bg-white text-white px-8 py-3 hover:bg-[#001588] hover:text-black transition-colors disabled:opacity-50" data-testid="lf-save">
            {busy ? "Saving…" : id ? "Save changes" : "Create listing"}
          </button>
          <button type="button" onClick={() => nav("/admin/listings")} className="border border-gray-300 px-8 py-3 hover:border-black">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function FormField({ label, value, onChange, type = "text", required, testId }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-gray-500 block mb-2">{label}</span>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} required={required}
        className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-[#050d19]"
        data-testid={testId}
      />
    </label>
  );
}
function FormSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-gray-500 block mb-2">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-[#050d19] bg-white" data-testid={testId}>
        {options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
      </select>
    </label>
  );
}
